import { useRef, useState } from 'react';
import { AlertTriangle, Pencil, Check, Repeat } from 'lucide-react';
import type { SessionRecord } from '@/api/sessions';
import { cn } from '@/lib/cn';
import { snapToQuarterHour } from '@/lib/calendar';

/*
 * Visual rules inherited from [client-legacy/js/calendar.js schedBuildBlockHTML]:
 *
 * - A colored dot before the client name encodes session status. The dot
 *   color must stay stable across the app — it's how staff scan a dense
 *   grid for cancellations without reading every tile.
 * - A small trailing icon encodes note-workflow state, but only for
 *   non-canceled sessions (a canceled session has no note to write).
 * - Full client name — per 2026-04-18 stakeholder feedback, the legacy
 *   "first + last initial" short-name paradigm is dropped app-wide
 *   because same-first-name collisions were common enough to confuse
 *   staff.
 * - Each block is tinted with the session type's configured color so
 *   the day column reads at a glance (DTT vs NET vs Supervision vs …).
 * - Series members get a Repeat icon in the header so staff can see at
 *   a glance which tiles belong to a recurring block.
 *
 * Drag-to-reschedule (Phase 2.2 step 9): pointer events track the gesture
 * directly rather than the HTML5 drag API — HTML5 drag's ghost image is
 * hard to style consistently across browsers and is flat-out unavailable
 * on most touch devices. Drop-target hit-testing uses the block's own
 * translated position (the top-left of the tile), not the cursor — users
 * expect "where I put the box" to win, per the 2026-04-18 feedback.
 * Dragging is always per-occurrence: even for series members, drag moves
 * this single session — bulk reschedule goes through the scope dialog.
 *
 * Edge resize: 6px drag handles at the top and bottom of the tile let
 * users adjust start/end without opening the form. Top edge slides the
 * start time (bottom fixed); bottom edge slides the end time (start
 * fixed). Both snap to 15 minutes, matching the grid resolution.
 */

const STATUS_DOT: Record<string, string> = {
    Scheduled: 'bg-info',
    Complete: 'bg-accent-dark',
    Finalized: 'bg-finalized',
    Canceled: 'bg-error',
};

function noteStatusIcon(noteStatus: string) {
    if (noteStatus === 'Not Started' || noteStatus === 'Missing') {
        return <AlertTriangle className="w-3 h-3 text-warning-text" aria-label={noteStatus} />;
    }
    if (noteStatus === 'Drafting' || noteStatus === 'Draft') {
        return <Pencil className="w-3 h-3 text-warning" aria-label={noteStatus} />;
    }
    if (noteStatus === 'Complete' || noteStatus === 'Signed') {
        return <Check className="w-3 h-3 text-info-text" aria-label={noteStatus} />;
    }
    return null;
}

/** Minimum pointer movement (px) before a click is interpreted as a drag. */
const DRAG_THRESHOLD_PX = 4;
/** Minimum session duration preserved by a resize gesture. */
const MIN_DUR_MIN = 15;

export interface SessionBlockPlacement {
    startSlot: number;
    heightSlots: number;
    lane: number;
    laneCount: number;
}

export interface SessionBlockDragResult {
    date: string;
    startH: number;
    startM: number;
}

export interface SessionBlockResizeResult {
    /** 'top' moves start time; 'bottom' moves end time. */
    edge: 'top' | 'bottom';
    startH: number;
    startM: number;
    dur: number;
}

export interface SessionBlockProps {
    session: SessionRecord;
    placement: SessionBlockPlacement;
    /** Pixel height of one 15-minute slot — matches the day column. */
    slotHeightPx: number;
    /** First visible hour on the grid. Used to convert y-pixels to a clock time. */
    gridStartHour: number;
    /** Last visible hour on the grid. Used to clamp drop targets. */
    gridEndHour: number;
    /** Color configured on the session's type; falls back to a neutral background. */
    typeColor?: string;
    onClick?: (session: SessionRecord) => void;
    /**
     * Called when the user drops this block on a day column. Not providing
     * this prop disables dragging (the block stays click-only).
     */
    onDragEnd?: (session: SessionRecord, result: SessionBlockDragResult) => void;
    /**
     * Called when the user releases a top/bottom edge resize. Not providing
     * this prop disables the resize handles.
     */
    onResizeEnd?: (session: SessionRecord, result: SessionBlockResizeResult) => void;
}

type GestureKind = 'drag' | 'resize-top' | 'resize-bottom';

export function SessionBlock({
    session, placement, slotHeightPx, gridStartHour, gridEndHour, typeColor,
    onClick, onDragEnd, onResizeEnd,
}: SessionBlockProps) {
    const isCanceled = session.status === 'Canceled';
    const dotClass = STATUS_DOT[session.status] ?? 'bg-neutral';
    const icon = isCanceled ? null : noteStatusIcon(session.noteStatus);
    const isSeries = !!session.seriesId;
    const title = `${session.client} · ${session.staff} · ${session.label}`;

    const startPtRef = useRef<{ x: number; y: number } | null>(null);
    const gestureRef = useRef<GestureKind>('drag');
    const wasActiveRef = useRef(false);
    const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number } | null>(null);
    const [resizeDelta, setResizeDelta] = useState<{ edge: 'top' | 'bottom'; dy: number } | null>(null);

    const dragEnabled = !!onDragEnd;
    const resizeEnabled = !!onResizeEnd;

    function beginGesture(e: React.PointerEvent<HTMLElement>, kind: GestureKind) {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        gestureRef.current = kind;
        startPtRef.current = { x: e.clientX, y: e.clientY };
        wasActiveRef.current = false;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }

    function handleBlockPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
        if (!dragEnabled) return;
        beginGesture(e, 'drag');
    }

    function handleResizePointerDown(
        e: React.PointerEvent<HTMLDivElement>,
        edge: 'top' | 'bottom',
    ) {
        if (!resizeEnabled) return;
        e.stopPropagation();
        beginGesture(e, edge === 'top' ? 'resize-top' : 'resize-bottom');
    }

    function handlePointerMove(e: React.PointerEvent<HTMLElement>) {
        const start = startPtRef.current;
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (!wasActiveRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        wasActiveRef.current = true;
        if (gestureRef.current === 'drag') {
            setDragOffset({ dx, dy });
        } else {
            setResizeDelta({
                edge: gestureRef.current === 'resize-top' ? 'top' : 'bottom',
                dy,
            });
        }
    }

    function handlePointerUp(e: React.PointerEvent<HTMLElement>) {
        const start = startPtRef.current;
        const hadStart = start !== null;
        const gesture = gestureRef.current;
        const el = e.currentTarget as HTMLElement;
        if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
        const wasActive = wasActiveRef.current;
        // Keep startPtRef populated during commit — commitDrag reads the
        // block's post-translate bounding rect, and commitResize reads the
        // original y to compute the net delta.
        setDragOffset(null);
        setResizeDelta(null);
        if (!hadStart || !wasActive || !start) {
            startPtRef.current = null;
            return;
        }

        if (gesture === 'drag' && onDragEnd) {
            commitDrag(e);
        } else if ((gesture === 'resize-top' || gesture === 'resize-bottom') && onResizeEnd) {
            commitResize(e, gesture === 'resize-top' ? 'top' : 'bottom', start.y);
        }
        startPtRef.current = null;
    }

    function commitDrag(e: React.PointerEvent<HTMLElement>) {
        if (!onDragEnd) return;
        // Hit-test by the block's translated top-left — not the cursor — so
        // users drop "where they put the box", per the 2026-04-18 UX
        // feedback. Temporarily disable pointer events on this block so
        // elementFromPoint hits the column below.
        const btn = e.currentTarget as HTMLElement;
        const prevPE = btn.style.pointerEvents;
        btn.style.pointerEvents = 'none';
        const rect = btn.getBoundingClientRect();
        const probeX = rect.left + rect.width / 2;
        const probeY = rect.top + 1;
        const under = document.elementFromPoint(probeX, probeY);
        btn.style.pointerEvents = prevPE;

        const col = under?.closest<HTMLElement>('[data-day-column]');
        if (!col) return;
        const targetDate = col.dataset.dayColumn ?? '';
        if (!targetDate) return;

        const colRect = col.getBoundingClientRect();
        // Round, not floor — snap the block's top edge to the nearest slot
        // line. With floor, a drop 1px short of a line would always fall
        // back to the previous slot, which felt like the tile was "fighting"
        // the cursor by 15 minutes. Matches the resize gesture's behavior.
        const slot = Math.max(0, Math.round((probeY - colRect.top) / slotHeightPx));
        const minutesFromStart = slot * 15;
        const maxStart = Math.max(0, (gridEndHour - gridStartHour) * 60 - session.dur);
        const clamped = snapToQuarterHour(Math.min(minutesFromStart, maxStart));
        const startH = gridStartHour + Math.floor(clamped / 60);
        const startM = clamped % 60;

        const unchanged =
            targetDate === session.date
            && startH === session.startH
            && startM === session.startM;
        if (unchanged) return;

        onDragEnd(session, { date: targetDate, startH, startM });
    }

    function commitResize(
        e: React.PointerEvent<HTMLElement>,
        edge: 'top' | 'bottom',
        startY: number,
    ) {
        if (!onResizeEnd) return;
        const dy = e.clientY - startY;
        const slotsChanged = Math.round(dy / slotHeightPx);
        if (slotsChanged === 0) return;
        const minutesChanged = slotsChanged * 15;

        let newStartH = session.startH;
        let newStartM = session.startM;
        let newDur = session.dur;

        if (edge === 'top') {
            // Top edge slides the start; end stays fixed, so dur shrinks by
            // the same amount the start advances.
            const origStartMin = session.startH * 60 + session.startM;
            const endMin = origStartMin + session.dur;
            const proposedStart = origStartMin + minutesChanged;
            const clampedStart = Math.max(
                gridStartHour * 60,
                Math.min(proposedStart, endMin - MIN_DUR_MIN),
            );
            const snapped = snapToQuarterHour(clampedStart - gridStartHour * 60) + gridStartHour * 60;
            newStartH = Math.floor(snapped / 60);
            newStartM = snapped % 60;
            newDur = endMin - snapped;
        } else {
            // Bottom edge slides dur only; start stays fixed.
            const startMin = session.startH * 60 + session.startM;
            const proposedEnd = startMin + session.dur + minutesChanged;
            const maxEnd = gridEndHour * 60;
            const clampedEnd = Math.max(startMin + MIN_DUR_MIN, Math.min(proposedEnd, maxEnd));
            const snapped = snapToQuarterHour(clampedEnd - startMin) + startMin;
            newDur = Math.max(MIN_DUR_MIN, snapped - startMin);
        }

        const unchanged =
            newStartH === session.startH
            && newStartM === session.startM
            && newDur === session.dur;
        if (unchanged) return;

        onResizeEnd(session, { edge, startH: newStartH, startM: newStartM, dur: newDur });
    }

    function handleClick() {
        if (wasActiveRef.current) {
            // Suppress the click that follows a drag/resize gesture. Reset
            // the flag so the next real click works normally.
            wasActiveRef.current = false;
            return;
        }
        onClick?.(session);
    }

    // Pixel positioning: lane drives the horizontal split, startSlot/height the vertical.
    // `calc(... - 2px)` leaves a 1px gap on each side so adjacent lanes don't visually merge.
    const style: React.CSSProperties = {
        top: placement.startSlot * slotHeightPx,
        height: placement.heightSlots * slotHeightPx,
        left: `calc(${(placement.lane / placement.laneCount) * 100}% + 1px)`,
        width: `calc(${(1 / placement.laneCount) * 100}% - 2px)`,
    };
    // Session-type color fills the block. Using inline style because session
    // types are per-tenant and we can't precompile arbitrary color classes
    // at build time. Type palette is intentionally pastel — dark text stays
    // readable on full-saturation backgrounds.
    if (typeColor) {
        style.backgroundColor = typeColor;
        style.borderColor = typeColor;
    }
    if (dragOffset) {
        style.transform = `translate(${dragOffset.dx}px, ${dragOffset.dy}px)`;
        style.zIndex = 50;
        style.opacity = 0.85;
        style.pointerEvents = 'auto';
    }
    if (resizeDelta) {
        // Visual preview of the resize: adjust top or height in-place so the
        // user can see where they're dropping the edge.
        const slotsChanged = Math.round(resizeDelta.dy / slotHeightPx);
        if (resizeDelta.edge === 'top') {
            style.top = (placement.startSlot + slotsChanged) * slotHeightPx;
            style.height = (placement.heightSlots - slotsChanged) * slotHeightPx;
        } else {
            style.height = (placement.heightSlots + slotsChanged) * slotHeightPx;
        }
        style.zIndex = 50;
        style.opacity = 0.9;
    }

    return (
        <button
            type="button"
            data-session-block="true"
            onClick={handleClick}
            onPointerDown={dragEnabled ? handleBlockPointerDown : undefined}
            onPointerMove={(dragEnabled || resizeEnabled) ? handlePointerMove : undefined}
            onPointerUp={(dragEnabled || resizeEnabled) ? handlePointerUp : undefined}
            onPointerCancel={(dragEnabled || resizeEnabled) ? handlePointerUp : undefined}
            title={title}
            className={cn(
                // `pointer-events-auto` overrides the parent wrapper's
                // `pointer-events-none` — the wrapper exists so the right-side
                // click gap on the day column can catch "create here" clicks
                // even when a block is layered underneath.
                'pointer-events-auto absolute rounded-sm border border-border bg-surface shadow-xs p-1 text-left overflow-hidden',
                'transition-colors hover:brightness-95 focus-visible:ring-2 focus-visible:ring-accent',
                dragEnabled && 'cursor-grab',
                dragEnabled && dragOffset && 'cursor-grabbing shadow-md',
                isCanceled && 'opacity-60 line-through decoration-text-subtle',
            )}
            style={style}
            aria-label={title}
        >
            {resizeEnabled && (
                <div
                    className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize"
                    onPointerDown={(e) => handleResizePointerDown(e, 'top')}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    aria-hidden
                />
            )}
            <div className="flex items-center gap-1 text-[11px] font-bold text-text truncate">
                <span className={cn('inline-block w-[7px] h-[7px] rounded-full shrink-0', dotClass)} aria-hidden />
                <span className="truncate">{session.client}</span>
                {isSeries && <Repeat className="w-3 h-3 shrink-0 text-text-muted" aria-label="Recurring" />}
                {icon && <span className="shrink-0">{icon}</span>}
            </div>
            <div className="text-[10px] text-text-muted truncate">{session.staff}</div>
            <div className="text-[10px] text-text-muted truncate">{session.label}</div>
            {resizeEnabled && (
                <div
                    className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize"
                    onPointerDown={(e) => handleResizePointerDown(e, 'bottom')}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    aria-hidden
                />
            )}
        </button>
    );
}
